export interface GeoIPResult {
  ip: string;
  lat: number;
  lng: number;
  city: string;
  country: string;
  countryCode: string;
}

const cache = new Map<string, GeoIPResult>();

async function lookupSingle(ip: string): Promise<GeoIPResult | null> {
  if (cache.has(ip)) return cache.get(ip)!;

  try {
    const res = await fetch(`https://ipwho.is/${ip}`);
    const data = await res.json();
    if (!data.success) return null;

    const result: GeoIPResult = {
      ip,
      lat: data.latitude,
      lng: data.longitude,
      city: data.city || 'Unknown',
      country: data.country || 'Unknown',
      countryCode: data.country_code || '',
    };
    cache.set(ip, result);
    return result;
  } catch {
    return null;
  }
}

/**
 * Resolve an array of IPs to geo coordinates.
 * Uses ipwho.is (free, CORS-friendly, no key needed).
 * Batches in groups of 10 with small delays to respect rate limits.
 */
export async function resolveIPs(ips: string[]): Promise<Map<string, GeoIPResult>> {
  const results = new Map<string, GeoIPResult>();
  const uncached = ips.filter((ip) => {
    const hit = cache.get(ip);
    if (hit) results.set(ip, hit);
    return !hit;
  });

  const batchSize = 10;
  for (let i = 0; i < uncached.length; i += batchSize) {
    const batch = uncached.slice(i, i + batchSize);
    const settled = await Promise.allSettled(batch.map(lookupSingle));
    settled.forEach((s, idx) => {
      if (s.status === 'fulfilled' && s.value) {
        results.set(batch[idx], s.value);
      }
    });
    if (i + batchSize < uncached.length) {
      await new Promise((r) => setTimeout(r, 200));
    }
  }

  return results;
}
